import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';

import type { AgentLine } from './agentLine';
import type { OrbSpeech } from './orb/DotOrb';
import { fitSpeechPlan, speechPlan, wordsShownAt, type SpeechPlan } from './orb/orbMotion';
import type { VoicePreference } from './voice/preference';
import { clipFor } from './voice/recorded';

const TICK_MS = 50;

export type Speaking = {
  line: AgentLine | null;
  speech: OrbSpeech | null;
  /** Words of `line` on screen so far; all of them once it has finished. */
  shown: number;
  /** The browser would not play sound until a tap: the words are on screen and the voice waits. */
  blocked: boolean;
  /** Says a line; `voice` overrides the one the hook was opened with (the owner just picked it). */
  say: (line: AgentLine, voice?: VoicePreference) => void;
  /** Silences the recording now; the words on screen carry on at their own pace. */
  stop: () => void;
};

/**
 * Says a line: the words appear in time with the orb's syllables and, when the
 * owner picked a voice, the recording of that line plays. The words start when
 * the sound does and last as long as the recording, so text and voice stay
 * together. A line without a recording is only text.
 */
export function useSpeech(voice: VoicePreference): Speaking {
  const [line, setLine] = useState<AgentLine | null>(null);
  const [speech, setSpeech] = useState<OrbSpeech | null>(null);
  const [shown, setShown] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const chosen = useRef(voice);
  const player = useRef<HTMLAudioElement | null>(null);
  const turn = useRef(0);

  useEffect(() => {
    chosen.current = voice;
  }, [voice]);

  const begin = useCallback((plan: SpeechPlan) => {
    setWaiting(false);
    if (prefersReducedMotion()) {
      setSpeech(null);
      setShown(plan.words.length);
      return;
    }
    setShown(0);
    setSpeech({ plan, startedAt: performance.now() });
  }, []);

  const say = useCallback(
    (next: AgentLine, override?: VoicePreference) => {
      const mine = ++turn.current;
      player.current?.pause();
      setLine(next);
      setSpeech(null);
      setBlocked(false);
      const which = override ?? chosen.current;
      const clip = which !== 'off' && next.voz && canPlayRecordedVoice() ? clipFor(which, next.voz) : null;
      const estimate = speechPlan(next.text);
      if (!clip) {
        begin(estimate);
        return;
      }
      const plan = fitSpeechPlan(estimate, clip.ms);
      setWaiting(true);
      play(player, clip.url).then(
        () => {
          if (turn.current === mine) begin(plan);
        },
        (error: unknown) => {
          if (turn.current !== mine) return;
          setBlocked(needsATap(error));
          begin(plan);
        },
      );
    },
    [begin],
  );

  const stop = useCallback(() => {
    player.current?.pause();
  }, []);

  useEffect(() => {
    if (!speech) return undefined;
    const id = window.setInterval(() => {
      const elapsed = performance.now() - speech.startedAt;
      setShown(wordsShownAt(speech.plan, elapsed));
      if (elapsed >= speech.plan.durationMs) setSpeech(null);
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [speech]);

  useEffect(
    () => () => {
      turn.current += 1;
      player.current?.pause();
    },
    [],
  );

  const visible = waiting ? 0 : speech ? shown : line ? countWords(line.text) : 0;
  return { line, speech, shown: visible, blocked, say, stop };
}

export function canPlayRecordedVoice(): boolean {
  return typeof window !== 'undefined' && typeof window.Audio === 'function';
}

function play(player: MutableRefObject<HTMLAudioElement | null>, url: string): Promise<void> {
  const audio = (player.current ??= new Audio());
  audio.src = url;
  return audio.play();
}

/** Browsers refuse sound the page did not ask for with a tap; that is not a broken clip. */
function needsATap(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'NotAllowedError';
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}
